
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { BlogPost } from '@/types/blog';
import { format } from 'date-fns';

interface BlogPostEditorProps {
  post?: BlogPost;
  isOpen: boolean;
  onClose: () => void;
  onSave: (post: BlogPost) => void;
  mode: 'create' | 'edit' | 'view';
}

const BlogPostEditor: React.FC<BlogPostEditorProps> = ({
  post,
  isOpen,
  onClose,
  onSave,
  mode
}) => {
  const isViewMode = mode === 'view';
  const isCreateMode = mode === 'create';
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('business');
  const [status, setStatus] = useState<'published' | 'draft' | 'scheduled'>('draft');
  const [socialShare, setSocialShare] = useState(false);
  const [autoScheduled, setAutoScheduled] = useState(false);
  
  useEffect(() => {
    if (post) {
      setTitle(post.title);
      setContent(post.content || '');
      setCategory(post.category);
      setStatus(post.status);
      setSocialShare(post.socialShare);
      setAutoScheduled(post.autoScheduled);
    } else {
      // Reset form for new post
      setTitle('');
      setContent('');
      setCategory('business');
      setStatus('draft');
      setSocialShare(true);
      setAutoScheduled(false);
    }
  }, [post, isOpen]);
  
  const handleSave = () => {
    const updatedPost: BlogPost = {
      id: post?.id || Date.now().toString(),
      title,
      content,
      category,
      status,
      date: post?.date || format(new Date(), 'yyyy-MM-dd'),
      author: post?.author || 'Admin User',
      image: post?.image || '/assets/e2e58de0-5228-4c21-8754-a52246e544e0.png',
      readTime: post?.readTime || '5 min',
      socialShare,
      autoScheduled
    };
    
    onSave(updatedPost);
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {isCreateMode ? 'Create New Blog Post' : 'Edit Blog Post'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid gap-4">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter post title"
            />
          </div>
          
          <div className="grid gap-4">
            <Label htmlFor="content">Content</Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={10}
              placeholder="Write your blog post content here..."
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={category}
                onValueChange={setCategory}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="trends">Trends</SelectItem>
                  <SelectItem value="customer">Customer Success</SelectItem>
                  <SelectItem value="ai-generated">AI Generated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={status}
                onValueChange={(value) => setStatus(value as 'published' | 'draft' | 'scheduled')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="social-share"
              checked={socialShare}
              onCheckedChange={setSocialShare}
            />
            <Label htmlFor="social-share">Share on social media</Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="auto-schedule"
              checked={autoScheduled}
              onCheckedChange={setAutoScheduled}
            />
            <Label htmlFor="auto-schedule">Enable AI auto-scheduling</Label>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            {isCreateMode ? 'Create Post' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BlogPostEditor;
